<template>
    <div class="-m-5 md:m-0 h-full flex-1 overflow-hidden">
        <tabulator
            :columns="columns"
            :actions="actions"
            :refresh.sync="refreshTable"
            :ajax-params="mailboxesParams"
            ajax-path="postfix/mailboxes"
            @ajaxResponse="(response) => {
                $store.commit('postfix/mailboxes/setItems', response);
            }"
        />
        <app-modal
            :show.sync="showEditModal"
            width="md:w-1/2 lg:w-1/3"
            @close="editModalClosed"
        >
            <template slot="header">Edit {{ editForm.username }}</template>
            <template slot="body">
                <app-input
                    v-model="editForm.uid"
                    placeholder="UID"
                    :error="editForm.errors.get('uid')"
                    @updated="editForm.errors.clear('uid')"
                    required
                />
                <app-input
                    v-model="editForm.username"
                    placeholder="Username"
                    :error="editForm.errors.get('username')"
                    @updated="editForm.errors.clear('username')"
                    required
                />
                <app-select                    
                    v-model="editForm.domain"
                    placeholder="Domain"
                    :error="editForm.errors.get('domain')"
                    @updated="editForm.errors.clear('domain')"
                    required
                    focus
                    :options="items.domains"
                    value-key="domain"
                    label-key="domain"                  
                /> 
                <app-input
                    v-model="editForm.maildir"
                    placeholder="Mail Directory"
                    :error="editForm.errors.get('maildir')"
                    @updated="editForm.errors.clear('maildir')"
                    required
                />
                <app-input
                    v-model="editForm.local_part"
                    placeholder="Local Part"
                    :error="editForm.errors.get('local_part')"
                    @updated="editForm.errors.clear('local_part')"
                    required
                />               
                <app-input
                    v-model="editForm.name"
                    placeholder="Name"
                    :error="editForm.errors.get('name')"
                    @updated="editForm.errors.clear('name')"
                    required
                />
                <app-input
                    v-model="editForm.quota"
                    placeholder="Quota (MB)"
                    :error="editForm.errors.get('quota')"
                    @updated="editForm.errors.clear('quota')"
                    required
                    integer
                />                
                <div class="mt-8 flex items-center justify-end">
                    <app-button 
                        class="btn-white" 
                        @click="showEditModal = false"
                        :disable="editFormIsLoading"
                    >Cancel</app-button>
                    <app-button 
                        class="btn-blue ml-3" 
                        @click="update"
                        :loading="editFormIsLoading"
                    >Save</app-button>
                </div>
            </template>
        </app-modal>
        <app-modal
            :show.sync="showAddModal"
            width="md:w-1/2 lg:w-1/4"
            @close="addModalClosed"
        >
            <template slot="header">Add Mailbox</template>
            <template slot="body">                
                <app-select
                    v-model="addForm.domain"
                    placeholder="Domain"
                    :error="addForm.errors.get('domain')"
                    @updated="addForm.errors.clear('domain')"
                    required
                    focus
                    :options="items.domains"
                    value-key="domain"
                    label-key="domain"                  
                />
                <app-input
                    v-model="addForm.username"
                    placeholder="Username"
                    :error="addForm.errors.get('username')"
                    @updated="addForm.errors.clear('username')"
                    alpha-dash
                    required
                />
                <app-input
                    type="password"
                    v-model="addForm.password"
                    placeholder="Password"
                    :error="addForm.errors.get('password')"
                    @updated="addForm.errors.clear('password')"
                    required
                    help-text="Password for POP3/IMAP"
                />

                <app-input
                    type="password"
                    v-model="addForm.password_confirmation"
                    placeholder="Confirm Password"
                    :error="addForm.errors.get('password_confirmation')"
                    @updated="addForm.errors.clear('password_confirmation')"
                    required
                />
                <app-input
                    v-model="addForm.name"
                    placeholder="Name"
                    :error="addForm.errors.get('name')"
                    @updated="addForm.errors.clear('name')"
                    required
                />
                <app-input
                    v-model="addForm.quota"
                    placeholder="Quota (MB)"
                    :error="addForm.errors.get('quota')"
                    @updated="addForm.errors.clear('quota')"
                    required
                    integer
                />
                <checkbox 
                    v-model="addForm.send_welcome_email"
                    class="mt-5"
                >Send welcome email</checkbox>             
                <div class="mt-8 flex items-center justify-end">
                    <app-button 
                        class="btn-white" 
                        @click="showAddModal = false"
                        :disable="addFormIsLoading"
                    >Cancel</app-button>
                    <app-button 
                        class="btn-blue ml-3" 
                        @click="store"
                        :loading="addFormIsLoading"
                    >Add</app-button>
                </div>
            </template>
        </app-modal>
        <app-modal
            :show.sync="showResetPasswordModal"
            width="md:w-1/2 lg:w-1/4"
        >
            <template slot="header">Reset Password</template>
            <template slot="body">
                <app-input
                    type="text"
                    v-model="resetPasswordForm.username"
                    placeholder="Email"
                    :error="resetPasswordForm.errors.get('username')"
                    @updated="resetPasswordForm.errors.clear('username')"
                    required
                    disable
                />
                <app-input
                    type="text"
                    v-model="resetPasswordForm.password"
                    placeholder="New Password"
                    :error="resetPasswordForm.errors.get('password')"
                    @updated="resetPasswordForm.errors.clear('password')"
                    required
                    :loading="resetPasswordFormGeneratingPassword"
                    :action="{
                        icon: () => import('@/icons/Refresh.vue'),
                        label: 'Regenerate Password',
                        handler: generatePassword
                    }"
                />
                <app-input 
                    type="text"
                    v-model="resetPasswordForm.mobile_number"
                    placeholder="Mobile Number"
                    :error="resetPasswordForm.errors.get('mobile_number')"
                    @updated="resetPasswordForm.errors.clear('mobile_number')"
                    help-text="The new password will be sent to this number."
                />
                <div class="mt-8 flex items-center justify-end">
                    <app-button 
                        class="btn-white" 
                        @click="showResetPasswordModal = false"
                        :disable="resettingPassword"
                    >Cancel</app-button>
                    <app-button 
                        class="btn-blue ml-3" 
                        @click="resetPassword"
                        :loading="resettingPassword"
                    >Reset Password</app-button>
                </div>
            </template>
        </app-modal>
        <app-modal
            :show.sync="showAliasModal"
            width="md:w-1/2 lg:w-1/3"
            @close="aliasModalClosed"
        >
            <template slot="header">Edit Forwarding Settings ({{ aliasForm.username }})</template>
            <template slot="body">
                <div class="flex">
                    <div class="mb-4 mt-2">To</div>
                    <div class="ml-3 flex-1">
                        <div
                            v-for="(goto, index) in aliasForm.goto"
                            class="mb-3"
                        >               
                            <div class="flex items-center">
                                <input                        
                                    v-model="aliasForm.goto[index]"
                                    class="border px-3 py-2 w-full rounded"
                                    placeholder="Email" 
                                />
                                <button 
                                    class="p-2 rounded hover:bg-red-600 bg-red-500 text-white ml-2 shadow"
                                    v-if="aliasForm.goto.length > 1"
                                    @click="deleteGoto(index)"
                                ><delete-icon width="20" height="20"/></button>
                                <button 
                                    v-if="index == (aliasForm.goto.length - 1)"
                                    class="p-2 rounded hover:bg-green-600 bg-green-500 text-white ml-2 shadow"
                                    @click="addGoto"
                                ><add-icon width="20" height="20"/></button>
                            </div>
                            <div 
                                class="text-red-500 mt-1" 
                                v-if="aliasForm.errors.has(`goto.${index}`)"
                            >{{ aliasForm.errors.get(`goto.${index}`) }}</div>
                        </div>
                        <checkbox v-model="aliasForm.deliver_to_local_mailbox">Deliver to local mailbox.</checkbox>
                    </div>
                </div>                           
                <div class="mt-8 flex items-center justify-end">
                    <app-button 
                        class="btn-white" 
                        @click="showAliasModal = false"
                        :disable="aliasFormIsLoading"
                    >Cancel</app-button>
                    <app-button 
                        class="btn-blue ml-3" 
                        @click="updateAlias"
                        :loading="aliasFormIsLoading"
                    >Save</app-button>
                </div>
            </template>
        </app-modal>
    </div>
</template>

<script>
    import { mapGetters } from 'vuex';
    import Form from '@/helpers/Form';
    import Http from '@/helpers/Http';

    export default {
        props: {
            domain: {
                type: String,
                default: null,
            }
        },
        data() {
            return {
                addForm: new Form({
                    domain: null,
                    username: null,
                    password: null,
                    password_confirmation: null,
                    name: null,
                    quota: 10,
                    send_welcome_email: true,
                    other_email: null,
                }),
                editForm: new Form({
                    uid: null,
                    username: null,
                    domain: null,
                    maildir: null,
                    local_part: null,
                    password: null,
                    password_confirmation: null,
                    name: null,
                    quota: null,
                    email_other: null,
                }),
                aliasForm: new Form({
                    username: null,
                    goto: [''],
                    deliver_to_local_mailbox: true,
                }),
                resetPasswordForm: new Form({
                    username: null,
                    password: null,
                    mobile_number: null,
                }),
                editFormIsLoading: false,
                addFormIsLoading: false,
                aliasFormIsLoading: false,
                resettingPassword: false,
                tableIsLoading: false,
                resetPasswordFormGeneratingPassword: false,
                showEditModal: false,
                showAddModal: false,
                showAliasModal: false,
                showResetPasswordModal: false,
                refreshTable: false,
                mailboxesParams: {},
                columns: [
                    {
                        title: 'UID',
                        field: 'uid',
                        sorter: 'number',
                        minWidth: 100,
                    },
                    {
                        title: 'Name',
                        field: 'name',
                        sorter: 'string',
                        minWidth: 200,
                    },
                    {
                        title: 'Email',
                        field: 'email',
                        sorter: 'string',
                        minWidth: 200,
                    },
                    {
                        title: 'Last Login',
                        field: 'last_login_for_human',
                        sorter: 'string',
                        minWidth: 200,
                    },                    
                    {
                        title: 'Status', 
                        field: 'active', 
                        formatter({_cell}) {
                            if (_cell.value) {
                                return '<span class="px-3 py-1 rounded-full shadow bg-green-200 text-green-700">Active</span>';
                            }

                            return '<span class="px-3 py-1 rounded-full shadow bg-red-200 text-red-700">Inactive</span>';
                        },
                        minWidth: 120,
                    },
                    {
                        title: 'Username',
                        field: 'username',
                        sorter: 'string',
                        minWidth: 200,
                    },
                    {
                        title: 'Domain',
                        field: 'domain',
                        sorter: 'string',
                        minWidth: 200,
                        visible: !this.domain,
                    },                    
                    {
                        title: 'To', 
                        field: 'goto_for_human', 
                        sorter: 'string',
                        minWidth: 200,
                    },                    
                    {
                        title: 'Maildir',
                        field: 'maildir',
                        sorter: 'string',
                        minWidth: 150,
                    },
                    {
                        title: 'Local Part',
                        field: 'local_part',
                        sorter: 'string',
                        minWidth: 200,
                    },                    
                ],
                actions: [
                    {
                        label: 'Edit',
                        icon: () => import('@/icons/EditOutlined.vue'),
                        handler: mailboxes => {
                            this.edit(mailboxes[0]);
                        },
                        requiredSelect: true,
                    },
                    {
                        label: 'Reset Password',
                        icon: () => import('@/icons/LockOutlined.vue'),
                        handler: items => {
                            this.showResetPasswordModal = true;
                            this.resetPasswordForm.errors.clear();
                            this.resetPasswordForm.reset();
                            this.resetPasswordForm.set({
                                username: items[0].email,
                            });

                            this.generatePassword();
                        }
                    },
                    {
                        label: 'Alias',
                        icon: () => import('@/icons/TextFormat.vue'),
                        requiredSelect: true,
                        handler: mailboxes => {
                            this.editAlias(mailboxes[0]);
                        }
                    },
                    {
                        label: 'Activate',
                        icon: () => import('@/icons/VisibilityOutlined.vue'),
                        requiredSelect: true,
                        handler: mailboxes => {
                            this.activate(mailboxes[0]);
                        },
                        isEligible(mailboxes) {
                            return !mailboxes[0].active;
                        }
                    },
                    {
                        label: 'Deactivate',
                        icon: () => import('@/icons/VisibilityOffOutlined.vue'),
                        requiredSelect: true,
                        handler: mailboxes => {
                            this.deactivate(mailboxes[0]);
                        },
                        isEligible(mailboxes) {
                            return mailboxes[0].active;
                        }
                    },
                    {
                        label: 'Delete',
                        icon: () => import('@/icons/DeleteOutlined.vue'),
                        handler: mailboxes => {
                            this.delete(mailboxes[0]);
                        },
                        requiredSelect: true,
                    },                    
                    {
                        label: 'Add',
                        icon: () => import('@/icons/Add.vue'),
                        handler: this.add,
                    }
                ],
            }
        },
        computed: {
            ...mapGetters({
                items: 'postfix/mailboxes/items',          
            }),
        },
        mounted() {
            this.mailboxesParams = {
                domain: this.domain,
            }
        },
        methods: {            
            edit(mailbox) {
                this.showEditModal = true;                
                this.editForm.set(JSON.parse(JSON.stringify(mailbox)));
                this.$store.commit('postfix/mailboxes/setSelected', mailbox);
            },
            editModalClosed() {
                this.editForm.reset();
                this.editForm.errors.clear();                
            },
            update() {
                this.editFormIsLoading = true;
                this.editForm.errors.clear();
                this.$store.dispatch('postfix/mailboxes/update', this.editForm.payload())
                    .then(() => {
                        this.editFormIsLoading = false;
                        this.showEditModal = false;
                        this.refreshTable = true;
                        this.$swal.fire({
                            type: 'success',
                            title: 'Success!',
                            text: 'Mailbox was updated successfully.'
                        })
                    })
                    .catch(({ response }) => {
                        this.editFormIsLoading = false;
                        this.editForm.errors.record(response.data);
                    });
            },
            addModalClosed() {
                this.addForm.reset();
                this.addForm.errors.clear();
            },
            add() {
                this.showAddModal = true;
                this.addForm.set({
                    goto: ['']
                });
            },
            store() {
                this.addFormIsLoading = true;
                this.addForm.errors.clear();
                this.$store.dispatch('postfix/mailboxes/store', this.addForm.payload())
                    .then(() => {
                        this.addFormIsLoading = false;
                        this.showAddModal = false;
                        this.refreshTable = true;
                        this.$swal.fire({
                            type: 'success',
                            title: 'Success!',
                            text: 'Mailbox was added successfully.'
                        })
                    })
                    .catch(({ response }) => {
                        this.addFormIsLoading = false;
                        this.addForm.errors.record(response.data);
                    })
            },
            deactivate(mailbox) {
                this.$swal.fire({
                    type: 'warning',
                    title: 'Are you sure?',
                    html: `You are about to deactivate "<strong>${mailbox.email}</strong>" mailbox.`,
                    showCancelButton: true,
                    confirmButtonColor: '#f56565',
                    confirmButtonText: 'Yes, deactivate it!',
                    showLoaderOnConfirm: true,
                    preConfirm: (response) => {
                        return this.$store.dispatch('postfix/mailboxes/deactivate', mailbox.email);
                    },
                    allowOutsideClick: () => !this.$swal.isLoading()
                })
                .then((result) => {
                    if (result.value) {
                        this.refreshTable = true;
                        this.$swal.fire({
                            type: 'success',
                            title: 'Success!',
                            text: 'Mailbox was successfully deactivated.'
                        })
                    }
                })                
            },
            activate(mailbox) {
                this.$swal.fire({
                    type: 'warning',
                    title: 'Are you sure?',
                    html: `You are about to activate "<strong>${mailbox.email}</strong>" mailbox.`,
                    showCancelButton: true,
                    confirmButtonColor: '#f56565',
                    confirmButtonText: 'Yes, activate it!',
                    showLoaderOnConfirm: true,
                    preConfirm: (response) => {
                        return this.$store.dispatch('postfix/mailboxes/activate', mailbox.email);
                    },
                    allowOutsideClick: () => !this.$swal.isLoading()
                })
                .then((result) => {
                    if (result.value) {
                        this.refreshTable = true;
                        this.$swal.fire({
                            type: 'success',
                            title: 'Success!',
                            text: 'Mailbox was successfully activated.'
                        })
                    }
                })                
            },
            delete(mailbox) {
                this.$swal.fire({
                    type: 'warning',
                    title: 'Are you sure?',
                    html: `You are about to delete "<strong>${mailbox.email}</strong>" mailbox permanently.`,
                    showCancelButton: true,
                    confirmButtonColor: '#f56565',
                    confirmButtonText: 'Yes, delete it!',
                    showLoaderOnConfirm: true,
                    preConfirm: (response) => {
                        return this.$store.dispatch('postfix/mailboxes/delete', mailbox.email);
                    },
                    allowOutsideClick: () => !this.$swal.isLoading()
                })
                .then((result) => {
                    if (result.value) {
                        this.refreshTable = true;
                        this.$swal.fire({
                            type: 'success',
                            title: 'Success!',
                            text: 'Mailbox was successfully deleted.'
                        })
                    }
                })                
            },
            addGoto() {
                this.aliasForm.goto.push('');
            },
            deleteGoto(index) {
                this.aliasForm.goto.splice(index, 1);
            },
            editAlias(mailbox) {
                const goto = mailbox.goto_array.map((email) => {
                    return email;
                });

                this.aliasForm.set({
                    username: mailbox.username,
                    deliver_to_local_mailbox: mailbox.deliver_to_local_mailbox,
                    goto: goto.length ? goto : ['']
                });
                
                this.showAliasModal = true;
            },
            aliasModalClosed() {
                this.aliasForm.errors.clear();
            },
            updateAlias() {
                this.aliasFormIsLoading = true;
                this.$store.dispatch('postfix/mailboxes/updateAlias', this.aliasForm.payload())
                    .then(() => {
                        this.aliasFormIsLoading = false;
                        this.showAliasModal = false;
                        this.refreshTable = true;
                        this.$swal.fire({
                            type: 'success',
                            title: 'Success!',
                            text: 'Alias was updated successfully.'
                        })
                    })
                    .catch(({ response }) => {
                        this.aliasFormIsLoading = false;
                        this.aliasForm.errors.record(response.data);
                    });
            },
            generatePassword() {
                this.resetPasswordFormGeneratingPassword = true;
                Http.get('password/strong')
                    .then(({data}) => {
                        this.resetPasswordFormGeneratingPassword = false;
                        this.resetPasswordForm.set({
                            password: data.password
                        });
                    });
            },
            resetPassword() {
                this.resettingPassword = true;
                Http.patch('postfix/mailboxes/password', this.resetPasswordForm.payload())
                    .then(({ data }) => {
                        this.resettingPassword = false;
                        this.showResetPasswordModal = false;
                        this.$swal.fire({
                            type: 'success',
                            title: 'Success!',
                            text: 'Password was successfully updated.'
                        })
                    })
                    .catch(({ response }) => {
                        this.resettingPassword = false;
                        this.resetPasswordForm.errors.record(response.data);
                    })
            }
        }
    }
</script>